import React, { useRef } from 'react';
import { XIcon } from 'lucide-react';
import { Button, Modal } from '@shared/components';
import { ElemisCheckoutFrame } from '@features/agreements';
import { useCheckoutFrameEvents, type CheckoutOutcome } from '../hooks/useCheckoutFrameEvents';

interface ElemisCheckoutModalProps {
    isOpen: boolean;
    onClose: () => void;
    checkoutUrl: string;
    applicationId: string | null;
    onOutcome: (outcome: CheckoutOutcome) => void;
}

/** Backdrop clicks never close it: an accidental dismissal mid-3DS is a lost
 *  sale. Owns the journey's one frame-message listener, a UI trigger only. */
export const ElemisCheckoutModal: React.FC<ElemisCheckoutModalProps> = ({
    isOpen,
    onClose,
    checkoutUrl,
    applicationId,
    onOutcome,
}) => {
    const closeWrapperRef = useRef<HTMLSpanElement>(null);
    const frameRef = useRef<HTMLIFrameElement>(null);
    useCheckoutFrameEvents({ checkoutUrl: isOpen ? checkoutUrl : null, applicationId, frameRef, onOutcome });

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            ariaLabel="Pay online with MotorPlan Payments"
            initialFocusRef={closeWrapperRef}
            showCloseButton={false}
            backdrop="dim"
            closeOnBackdropClick={false}
            className="flex h-dvh max-w-[700px] flex-col overflow-hidden max-sm:rounded-none sm:h-[90vh]"
            childrenClassName="flex min-h-0 flex-1 flex-col"
        >
            <div className="flex h-10 shrink-0 items-center justify-end px-1.5">
                <span ref={closeWrapperRef}>
                    <Button
                        onClick={onClose}
                        variant="neutral"
                        appearance="ghost"
                        size="none"
                        className="size-7 rounded-full"
                        startIcon={<XIcon size={18} />}
                        ariaLabel="Close payment checkout"
                    />
                </span>
            </div>

            <div className="min-h-0 flex-1">
                <ElemisCheckoutFrame ref={frameRef} checkoutUrl={checkoutUrl} />
            </div>
        </Modal>
    );
};
